import React from "react";
import {
  useGetMyPendingDelegationsQuery,
  useAcceptDelegationMutation,
  useRejectDelegationMutation
} from "../../utils/api/endpoints/delegationsApi";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../utils/api/endpoints/user/credentials";

const DelegationsBanner: React.FC = () => {
  const { role } = useUserCredentials();
  const eligibleRoles = [
    UserRole.RESEARCHER,
    UserRole.ADMIN,
    UserRole.SUB_ADMIN,
    UserRole.ADMIN_MANAGER,
    UserRole.QA
  ];
  const canViewDelegations = role !== undefined && eligibleRoles.includes(role as UserRole);

  const { data, isFetching, isError } = useGetMyPendingDelegationsQuery(undefined, { skip: !canViewDelegations });
  const [acceptDelegation, { isLoading: accepting }] = useAcceptDelegationMutation();
  const [rejectDelegation, { isLoading: rejecting }] = useRejectDelegationMutation();

  if (!canViewDelegations) return null;

  const delegations = data?.data || [];

  if (isError || delegations.length === 0) return null;

  const handleAccept = async (id: string) => {
    try {
      await acceptDelegation({ id }).unwrap();
    } catch (e) {
      console.error(e);
    }
  };

  const handleReject = async (id: string) => {
    try {
      await rejectDelegation({ id }).unwrap();
    } catch (e) {
      console.error(e);
    }
  };

  const getReportInfo = (d: any) => {
    if (d.scope === "retest" && d.Retest?.report) {
      return d.Retest.report;
    }
    return d.Report;
  };

  const getOriginalResearcherName = (d: any) => {
    const report = getReportInfo(d);
    if (report?.user) {
      return report.user.display_name || report.user.username || "Unknown";
    }
    return "Unknown";
  };

  return (
    <div className="mx-4 mt-4 rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-md">
      <div className="px-6 py-4">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Pending Delegations</h3>
            <p className="text-sm text-gray-600 mt-1">You have {delegations.length} delegation request{delegations.length !== 1 ? 's' : ''} awaiting your action</p>
          </div>
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100">
            <span className="text-lg font-bold text-blue-600">{delegations.length}</span>
          </div>
        </div>

        <div className="space-y-3">
          {delegations.map(d => {
            const report = getReportInfo(d);
            const expiresAt = d.expires_at ? new Date(d.expires_at) : null;
            const isExpiringSoon = expiresAt && (expiresAt.getTime() - Date.now()) < 3600000; // Less than 1 hour

            return (
              <div key={d.id} className="flex flex-col gap-3 rounded-lg bg-white p-4 border border-gray-100 hover:shadow-md transition-shadow">
                {/* Header with scope and type */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                      d.scope === "retest"
                        ? "bg-indigo-100 text-indigo-700"
                        : "bg-sky-100 text-sky-700"
                    }`}>
                      {d.scope === "retest" ? "Retest" : "Report Comment"}
                    </span>
                    <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {d.type === "auto" ? "Auto" : "Manual"}
                    </span>
                  </div>
                  {isExpiringSoon && (
                    <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-1 rounded">
                      Expiring soon
                    </span>
                  )}
                </div>

                {/* Report/Retest details */}
                {report && (
                  <div className="space-y-2 text-sm">
                    <div>
                      <p className="text-gray-500 text-xs uppercase tracking-wide">Report</p>
                      <p className="font-medium text-gray-900 truncate" title={report.report_title}>
                        {report.report_title}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-gray-500 text-xs uppercase tracking-wide">Program</p>
                        <p className="font-medium text-gray-900 truncate" title={report.program?.program_title}>
                          {report.program?.program_title || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs uppercase tracking-wide">Original Researcher</p>
                        <p className="font-medium text-gray-900 truncate" title={getOriginalResearcherName(d)}>
                          {getOriginalResearcherName(d)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Footer with expiry and actions */}
                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <span className={`text-xs font-medium ${isExpiringSoon ? "text-red-600" : "text-gray-500"}`}>
                    {expiresAt ? `Expires ${expiresAt.toLocaleString()}` : "Expires soon"}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      className="px-4 py-1.5 rounded-md bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                      disabled={accepting || rejecting}
                      onClick={() => handleAccept(d.id)}
                    >
                      Accept
                    </button>
                    <button
                      className="px-4 py-1.5 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                      disabled={accepting || rejecting}
                      onClick={() => handleReject(d.id)}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DelegationsBanner;

